import React, { useEffect, useRef } from "react";
import { UseBooleanReturn } from "../../custom-hooks/useBoolean";
import "./prodeseg-dropdown.scss";
import { ClickableDiv } from "../ClickableDiv";
import { ProdesegDropdownContextProvider, useProdesegDropdownContext } from "./ProdesegDropdownContextProvider";
import { DISPLAY_NAME_DROPDOWN_MENU, DISPLAY_NAME_DROPDOWN_TOGGLE } from "./prodeseg-dropdown.constants";
import { useWindowBreakpoints } from "../../custom-hooks/useWindowBreakpoints";
import { ReactComponent as IconArrowUp } from "../../assets/img/icon/arrow_up.svg";

interface ProdesegDropdownProps extends Omit<React.ComponentProps<"article">, "children"> {
    closeOnInteraction?: boolean;
    children?: React.ReactNode | React.FunctionComponent<UseBooleanReturn>;
    activateModal?: boolean;
    position?: "top" | "bottom";
    align?: "center" | "left" | "right";
    arrowClassName?: string;
    disableBodyScroll?: boolean;
}

export const ProdesegDropdown: React.FC<ProdesegDropdownProps> = props => {
    return (
        <ProdesegDropdownContextProvider>
            <Dropdown {...props} />
        </ProdesegDropdownContextProvider>
    );
};

const Dropdown: React.FC<ProdesegDropdownProps> = ({
    children,
    className = "",
    closeOnInteraction = false,
    activateModal,
    position = "bottom",
    align = "right",
    arrowClassName = "",
    disableBodyScroll = false,
    ...props
}) => {
    const dropdownState = useProdesegDropdownContext();
    const { isDownSm } = useWindowBreakpoints();
    const dropdownRef = useRef<HTMLElement>(null);
    const { value, setFalse, toggle } = dropdownState;

    const isTop = position === "top";
    const isBottom = position === "bottom";
    const showArrow = !isDownSm && value;

    const childrenComponents = React.Children.toArray(
        typeof children === "function" ? children(dropdownState)?.props?.children : children
    );

    const isChildByDisplayName = (child: typeof children, componentDisplayName: string) => {
        return (
            React.isValidElement(child) &&
            !["string", "number"].includes(typeof child?.type) &&
            // @ts-ignore
            child?.type?.displayName === componentDisplayName
        );
    };

    const toggleComponent = childrenComponents.find(child => isChildByDisplayName(child, DISPLAY_NAME_DROPDOWN_TOGGLE));
    const menuComponent = childrenComponents.find(child => isChildByDisplayName(child, DISPLAY_NAME_DROPDOWN_MENU));

    useEffect(() => {
        return () => setFalse();
    }, []);

    useEffect(() => {
        !activateModal && value && !isDownSm
            ? document.addEventListener("click", handleClickOutside)
            : document.removeEventListener("click", handleClickOutside);

        return () => document.removeEventListener("click", handleClickOutside);
    }, [value, isDownSm, activateModal]);

    useEffect(() => {
        if (disableBodyScroll && value) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [disableBodyScroll, value]);

    const handleClickOutside = (event: Event) => {
        if (!dropdownRef.current?.contains?.(event.target as Node)) setFalse();
    };

    const ToggleComponent = (
        <ClickableDiv cursor onClick={toggle}>
            {toggleComponent}
        </ClickableDiv>
    );

    const IconArrow = React.createElement(IconArrowUp, {
        className: `prodeseg-dropdown__arrow ${isTop ? "bottom-100" : "top-100 start-50"} ${arrowClassName}`,
        style: { rotate: isTop ? "180deg" : "", transform: "translate(-50%)" },
    });

    return (
        <article className={`prodeseg-dropdown__wrap ${className}`} {...props} ref={dropdownRef}>
            {isTop && showArrow && IconArrow}
            {isBottom && ToggleComponent}
            <ClickableDiv onClick={() => (closeOnInteraction ? setFalse() : null)}>
                <div
                    className={`prodeseg-dropdown__menu prodeseg-dropdown__menu--${position} prodeseg-dropdown__menu--${align} 
                                ${value ? "d-block" : "d-none"}`}
                >
                    {menuComponent}
                </div>
            </ClickableDiv>
            {isTop && ToggleComponent}
            {isBottom && showArrow && IconArrow}
        </article>
    );
};
